import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema, PctStringSchema } from './primitives.js';
const c = initContract();
/**
 * Payment Terms (PART 5, MD-09) — the tenant's own list of deadlines, "so the
 * system can work out a due date instead of somebody typing one" (§1).
 *
 * Four ways of working (field 2): due immediately · days after the document
 * date · end of the month after · a fixed day of the following month. The
 * arithmetic is `dueDateFromTerm` in the API (BR-MD-09-03..05); this file
 * carries the shapes and the spec's own sentences.
 *
 * Permission area: `accounting` — MD-09 §2 gives the Accountant everything
 * here and §9 makes it one screen behind one permission (owner ruling R1;
 * role names are illustrative per MD-RULE-10). The screen lives under
 * General Settings (§3).
 */
// ── The fixed sentences of MD-09 ────────────────────────────────────────────
export const PAYMENT_TERM_MESSAGES = {
    /** BR-MD-09-01. */
    nameRequired: 'Type a name for this payment term.',
    /** MD-09 §15 "Duplicate: duplicate names are refused." */
    nameTaken: (name) => `There is already a payment term called ${name}.`,
    /** ERR-MD-09-01 / BR-MD-09-02. */
    daysRange: 'Days must be between 0 and 365.',
    /** ERR-MD-09-02 / BR-MD-09-08. */
    discountDaysRequired: 'Say how many days count as early.',
    /** ERR-MD-09-04 / BR-MD-09-07 — the spec's own sentence. */
    inUse: (name, count) => `${name} is used by ${count} ${count === 1 ? 'customer' : 'customers'} and cannot be deleted. Switch it off instead.`,
    /** BR-MD-09-04 — a due date must never be earlier than its document date. */
    dueBeforeDocument: 'The due date must be on or after the document date.',
};
/**
 * D46 — the payment-term family's CONVERTED parameterised refusals, structured.
 * `inUse`'s count is a genuine customer count (raw; noun-only customer/
 * customers plural); `nameTaken` carries a display name. Sentence parity pinned
 * by `refusal-census.spec.ts`.
 */
export const PAYMENT_TERM_REFUSALS = {
    inUse: (name, count) => ({
        message: PAYMENT_TERM_MESSAGES.inUse(name, count),
        key: 'paymentTerms.errInUse',
        params: { name, count },
    }),
    nameTaken: (name) => ({
        message: PAYMENT_TERM_MESSAGES.nameTaken(name),
        key: 'paymentTerms.errNameTaken',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
/** Field 2's four choices, verbatim from MD-09 §5. */
export const PaymentTermMethodSchema = z.enum([
    'immediate',
    'days_after',
    'end_of_next_month',
    'day_of_month',
]);
export const PaymentTermSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    method: PaymentTermMethodSchema,
    days: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    /** Field 5 — "0.00" means none. Decimal string (§2.1). */
    earlyDiscountPct: z.string(),
    discountDays: z.number().int().nullable(),
    /** Fields 7–8 (BR-MD-09-06): at most one of each. */
    defaultForCustomers: z.boolean(),
    defaultForVendors: z.boolean(),
    isActive: z.boolean(),
    /** The delete pre-check — partners carrying this term. */
    partnersOn: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
const TermFieldsSchema = z.object({
    name: z.string().trim().min(1, PAYMENT_TERM_MESSAGES.nameRequired).max(60),
    method: PaymentTermMethodSchema,
    /** BR-MD-09-02 / ERR-MD-09-01 — whole days, 0..365, never below zero. */
    days: z.number().int().min(0, PAYMENT_TERM_MESSAGES.daysRange).max(365, PAYMENT_TERM_MESSAGES.daysRange).optional(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    earlyDiscountPct: PctStringSchema.optional(),
    discountDays: z.number().int().min(1).max(365).optional(),
    defaultForCustomers: z.boolean().default(false),
    defaultForVendors: z.boolean().default(false),
});
/**
 * The conditional requirements of fields 3, 4 and 6 — BR-MD-09-01 ("a name
 * and a way of working"), field 3's "Yes when Days after", field 4's "Yes
 * when Day of the month", BR-MD-09-08's discount days. Checked here on
 * CREATE; on PATCH the service re-checks the MERGED row (and the table's own
 * CHECK constraints are the race-proof floor either way).
 */
export const CreatePaymentTermRequestSchema = TermFieldsSchema.strict()
    .refine((v) => v.method !== 'days_after' || v.days !== undefined, {
    message: PAYMENT_TERM_MESSAGES.daysRange,
    path: ['days'],
})
    .refine((v) => v.method !== 'day_of_month' || v.dayOfMonth !== undefined, {
    message: 'Say which day of the month.',
    path: ['dayOfMonth'],
})
    .refine((v) => v.earlyDiscountPct === undefined ||
    Number(v.earlyDiscountPct) === 0 ||
    v.discountDays !== undefined, { message: PAYMENT_TERM_MESSAGES.discountDaysRequired, path: ['discountDays'] });
export const UpdatePaymentTermRequestSchema = z
    .object({
    name: z.string().trim().min(1, PAYMENT_TERM_MESSAGES.nameRequired).max(60).optional(),
    method: PaymentTermMethodSchema.optional(),
    days: z
        .number()
        .int()
        .min(0, PAYMENT_TERM_MESSAGES.daysRange)
        .max(365, PAYMENT_TERM_MESSAGES.daysRange)
        .nullable()
        .optional(),
    dayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
    earlyDiscountPct: PctStringSchema.optional(),
    discountDays: z.number().int().min(1).max(365).nullable().optional(),
    defaultForCustomers: z.boolean().optional(),
    defaultForVendors: z.boolean().optional(),
})
    .strict();
const termParams = z.object({ termId: z.uuid() }).strict();
export const paymentTermsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/payment-terms',
        query: z
            .object({
            q: z.string().optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ terms: PaymentTermSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The term list (accounting.view) — MD-09 §4: Name, How it works, Days, status. A short screen; unpaged like every vocabulary (PAGINATION.md c).',
    },
    get: {
        method: 'GET',
        path: '/api/payment-terms/:termId',
        pathParams: termParams,
        responses: {
            200: PaymentTermSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One term (accounting.view).',
    },
    create: {
        method: 'POST',
        path: '/api/payment-terms',
        body: CreatePaymentTermRequestSchema,
        responses: {
            201: PaymentTermSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New term (accounting.create). Duplicate names refused; a new default unticks the previous (BR-MD-09-06, the ERR-MD-09-03 confirm is the UI’s).',
    },
    update: {
        method: 'PATCH',
        path: '/api/payment-terms/:termId',
        pathParams: termParams,
        body: UpdatePaymentTermRequestSchema,
        responses: {
            200: PaymentTermSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a term (accounting.edit). BR-MD-09-05: no POSTED document moves — due dates are stored on documents, and drafts re-read the term.',
    },
    deactivate: {
        method: 'POST',
        path: '/api/payment-terms/:termId/deactivate',
        pathParams: termParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PaymentTermSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch off (accounting.edit) — not pickable on anything new; existing documents keep their due dates (MD-09 §7).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/payment-terms/:termId/reactivate',
        pathParams: termParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PaymentTermSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (accounting.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/payment-terms/:termId',
        pathParams: termParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (accounting.delete) — refused while any partner carries the term, with ERR-MD-09-04’s sentence (BR-MD-09-07); the RESTRICT FK refuses even a bug here.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=payment-terms.js.map